/** @type {import("./types.js").VisualGuidesCatalog} */
export const visualGuidesEn = {
  "how": {
    "intro": "From standard sheet to work plan in four steps. Under each step you see where that lives in Plaatplan.",
    "steps": [
      {
        "title": "Set up material stock",
        "body": "On the left in the planner, choose material and sheet size. Set kerf width and grain direction. Add offcut stock you already have if needed."
      },
      {
        "title": "Enter subprojects and parts",
        "body": "Below the cut layout, create a subproject per piece of furniture or job. Enter panels via the table or with a line like 600×1220×2 side panel."
      },
      {
        "title": "Review the cut layout",
        "body": "In the center you see how panels lie on the sheets, color-coded per project. On the left under Results are the sheet count and utilization."
      },
      {
        "title": "Open the work plan",
        "body": "Click Export → Open work plan. You go to the work plan with two tabs: Prepare (configure) and Shop order (take to the store).",
        "note": "/handleiding/werkplan-printen",
        "noteLabel": "Guide: Print work plan"
      }
    ]
  },
  "werkplanExport": {
    "intro": "The work plan is meant to take to the hardware store or cutting service, or to saw yourself. You open it from the planner, configure everything under Prepare, review the Shop order, and save as PDF via your browser.",
    "steps": [
      {
        "title": "Open the work plan",
        "body": "First add parts in the planner. Then click Export in the header and choose Open work plan. Your work plan opens on a separate page with the Prepare and Shop order tabs."
      },
      {
        "title": "Prepare",
        "body": "Choose which sheets are in the order (new standard sheets and/or offcut stock). Set cut settings, adjust the strategy per sheet, and refine the cut list (dimensions, instructions). Optional: review the cut sequence on desktop.",
        "note": "/handleiding/zaagvolgorde-controleren",
        "noteLabel": "Guide: Review cut sequence"
      },
      {
        "title": "Review the shop order",
        "body": "Switch to the Shop order tab. This is what you take or print: cut list, general instruction, and visual layout. Check that dimensions and strategy are correct before you print."
      },
      {
        "title": "Print or save as PDF",
        "body": "Click Print / save as PDF. The app automatically switches to Shop order. In your browser's print dialog, choose Save as PDF or a printer. On paper or PDF, only the work plan pages are included."
      }
    ],
    "sections": [
      {
        "heading": "What is in the work plan?",
        "paragraphs": [
          "Cut list with dimensions, quantities and edge finishing per panel. Visual layout per sheet. Optionally offcuts or strips, depending on the chosen strategy.",
          "Under Prepare you can adjust everything. The Shop order tab shows the clean version for the cutting service or your own saw table.",
          "The work plan is vendor-neutral: usable at Hornbach, Gamma, Karwei or any other cutting service."
        ]
      },
      {
        "heading": "Cut strategies",
        "paragraphs": [
          "Efficient cutting, keep large offcut and deliver strips change the instructions and the work plan, not which panels sit where on the sheet.",
          "Choose a strategy per sheet if you want to give the cutting service a different approach. Turn on Show cut lines to see numbered cuts. Optionally on desktop you can review the cut sequence step by step."
        ]
      },
      {
        "heading": "Not the same as CSV or project file",
        "paragraphs": [
          "CSV is handy for a spreadsheet or email. The project file (.json) is your backup to continue working later in Plaatplan.",
          "For the cut shop or your own saw table, use the printable work plan."
        ]
      }
    ]
  },
  "cutSimulator": {
    "intro": "Review the numbered cut sequence at home on desktop before you print or go to the hardware store. You find this option in the work plan, Prepare tab, for each sheet under Visual cut layout. On a narrow screen you only see the static sheet diagram.",
    "steps": [
      {
        "title": "Open the work plan",
        "body": "Open the work plan via Export → Open work plan in the planner. Go to the Prepare tab and scroll to Visual cut layout. The simulator sits per sheet, below the strategy settings.",
        "note": "/handleiding/werkplan-printen",
        "noteLabel": "Guide: Print work plan"
      },
      {
        "title": "Turn on cut lines",
        "body": "Under Cut settings, turn on Show cut lines (numbered, per cut strategy). Without this option you see no numbered cuts and cannot use the simulator. The chosen cut strategy determines which cuts are shown."
      },
      {
        "title": "Open the simulator",
        "body": "Above each sheet visual is a Review cut sequence strip. Click View sequence. Only one simulator can be open per work plan page at a time: if you open it on sheet 2, it closes on sheet 1."
      },
      {
        "title": "Step through cuts",
        "body": "Use Previous and Next, the slider or ← and → on the keyboard. The current cut is orange; earlier cuts are dimmed. The active piece gets an orange border; the rest of the sheet is dimmed. Below the diagram each step has a short description of the cut."
      },
      {
        "title": "Close and continue",
        "body": "Press Esc or close the simulator to return to the static sheet diagram. Then review the Shop order tab before you print."
      }
    ],
    "sections": [
      {
        "heading": "What is this for?",
        "paragraphs": [
          "This is a preparation tool: you see in which order the numbered cuts are logically carried out and which part of the sheet belongs to each cut.",
          "Not a live saw coach at the machine. At the saw table you remain responsible for safety and verification yourself."
        ]
      },
      {
        "heading": "Desktop and narrow screens",
        "paragraphs": [
          "The step-by-step view works on a wide screen (desktop or tablet in landscape). On a phone or narrow browser window you still see the sheet diagram with numbered cuts, but not the interactive walkthrough."
        ]
      },
      {
        "heading": "Keyboard",
        "paragraphs": [
          "← and →: previous or next cut. Esc: close simulator. Keys only work when the simulator is open and you are not typing in a text field."
        ]
      },
      {
        "heading": "Strategy and sheet",
        "paragraphs": [
          "Cuts follow the chosen cut strategy per sheet (efficient cutting, keep large offcut or deliver strips). If you switch strategy or sheet, the simulator starts again at step 0.",
          "When delivering strips or when there are no cuts, the simulator may show a short message instead of steps."
        ]
      }
    ]
  }
};
